import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Stack, Tab, Tabs, Typography } from '@mui/material';

const styles = {
  tabs: ({ palette, breakpoints }) => ({
    width: '100%',
    borderRadius: '24px',
    border: `2px solid ${palette.text.primary}`,
    '.MuiTabs-indicator': {
      display: 'none',
    },
    '.MuiTabs-flexContainer': {
      height: 42,

      [breakpoints.up('md')]: {
        height: 48,
      },
    },
  }),
  tab: ({ palette }, isActive) => ({
    backgroundColor: isActive ? palette.text.primary : palette.common.white,
    minHeight: 44,
    transition: 'background-color 0.3s',
    '&:first-of-type': { borderRadius: '0 24px 24px 0' },
    '&:last-of-type': { borderRadius: '24px 0 0 24px' },
  }),
};

export const DashboardTabs = ({ onClick = () => {}, children = [], defaultActiveTabTitle, disabled = false }) => {
  const [selectedTab, setSelectedTab] = useState(defaultActiveTabTitle);

  const isSelectedTabAvailable = children.some((item) => item.props.title === selectedTab);
  const activeTab = isSelectedTabAvailable ? selectedTab : defaultActiveTabTitle;

  const activeTabContent = useMemo(
    () => children.find((item) => item.props.title === activeTab),
    [activeTab, children],
  );

  return (
    <Stack justifyContent="flex-start" alignItems="center" width="100%" height="100%">
      <Tabs
        variant="fullWidth"
        value={activeTab}
        onChange={(event, value) => {
          setSelectedTab(value);
          onClick?.(value);
        }}
        aria-label="Dashboard tabs"
        sx={styles.tabs}
      >
        {children.map((item) => {
          const tabName = item.props.title;
          const isActive = activeTab === tabName;

          return (
            <Tab
              key={tabName}
              value={tabName}
              label={
                <Typography
                  variant={isActive ? 'button' : 'body1'}
                  component="span"
                  color={isActive ? 'text.secondary' : 'text.primary'}
                >
                  {item.props.title}
                </Typography>
              }
              id={`dashboard-tab-${tabName}`}
              aria-controls={`dashboard-tabpanel-${tabName}`}
              disabled={disabled}
              sx={(theme) => styles.tab(theme, isActive)}
            />
          );
        })}
      </Tabs>
      {activeTabContent}
    </Stack>
  );
};

DashboardTabs.propTypes = {
  children: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.object])),
  onClick: PropTypes.func,
  defaultActiveTabTitle: PropTypes.string.isRequired,
  disabled: PropTypes.bool,
};
